/*
 * This file is the entry point for the external H5 integration
 * Entry point for created H5 session for profile association
 */
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { baseURL } from '../../api/url';
import backgroundImage from '../../assets/background.jpg';

type SessionBootstrapResponse = {
  success: boolean;
  data?: {
    session_id: string;
    session_mode: 'login' | 'register';
    cust_id: string;
    prefill?: {
      phone_number?: string;
      country_code?: string;
      full_name?: string;
      email?: string;
      data?: Record<string, unknown>;
    };
    is_registered: boolean;
    expires_at: string;
  };
  error?: {
    code?: string;
    message?: string;
  };
};

export default function SessionEntryPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const sessionQuery = useQuery<SessionBootstrapResponse>({
    queryKey: ['external-session-bootstrap', sessionId],
    enabled: !!sessionId,
    queryFn: async () => {
      const url = `${baseURL}/v1/external/session/bootstrap?session_id=${encodeURIComponent(
        sessionId as string
      )}`;
      const response = await fetch(url);
      return response.json();
    },
    retry: false
  });

  useEffect(() => {
    if (!sessionId || !sessionQuery.data?.success || !sessionQuery.data?.data) {
      return;
    }

    const sessionData = sessionQuery.data.data;

    sessionStorage.setItem('external_session_id', sessionData.session_id || '');
    sessionStorage.setItem('external_cust_id', sessionData.cust_id || '');
    sessionStorage.setItem(
      'external_prefill_phone_number',
      sessionData.prefill?.phone_number || ''
    );
    sessionStorage.setItem(
      'external_prefill_country_code',
      sessionData.prefill?.country_code || ''
    );
    sessionStorage.setItem(
      'external_prefill_full_name',
      sessionData.prefill?.full_name || ''
    );
    sessionStorage.setItem(
      'external_prefill_email',
      sessionData.prefill?.email || ''
    );
    sessionStorage.setItem(
      'external_session_expires_at',
      sessionData.expires_at || ''
    );

    navigate('/login', {
      replace: true
    });
  }, [navigate, sessionId, sessionQuery.data]);

  const isExpired =
    sessionQuery.data?.error?.code === 'SESSION_EXPIRED' ||
    (!sessionQuery.isLoading &&
      sessionQuery.data?.success === false &&
      sessionQuery.data?.error?.code === 'SESSION_EXPIRED');

  const hasError =
    !sessionId ||
    sessionQuery.isError ||
    (!sessionQuery.isLoading && sessionQuery.data?.success === false);

  return (
    <div
      className="flex min-h-screen flex-col items-center justify-center bg-white"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {sessionQuery.isLoading ? (
        <div className="flex flex-col items-center gap-4">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-[#5CB891] border-t-transparent" />
          <p className="text-lg font-medium text-gray-700">
            Validating your session...
          </p>
        </div>
      ) : hasError ? (
        <div className="rounded-xl bg-white/90 px-6 py-5 text-center shadow">
          <p className="text-xl font-semibold text-gray-900">
            {isExpired ? 'Session expired' : 'Invalid session'}
          </p>
          <p className="mt-2 text-sm text-gray-600">
            {isExpired
              ? 'Please request a new session from your app and try again.'
              : 'Unable to start your registration session.'}
          </p>
        </div>
      ) : null}
    </div>
  );
}
